import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { t } from 'i18next';

import { initialPeriodsTableRow, IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';

export const useGetCurrentAssetsVariants = (isExamplePage: boolean) => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    return [
     isExamplePage ? 'Cash and cash equivalents' : t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.cashEquivalents'),
     isExamplePage ? 'Short-term investments' : t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.shortTermInvestments'),
     isExamplePage ? 'Accounts receivable' : t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.accountsReceivable'),
    isExamplePage ? 'Inventory' :  t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.inventory'),
     isExamplePage ? 'Prepaid expenses' : t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.prepaidExpenses'),
     isExamplePage ? 'Other current assets' : t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.otherCurrentAssets'),
    ];
  }, [t, i18n, isExamplePage]);
};
export const useGetNonCurrentAssetsVariants = (isExamplePage: boolean) => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    return [
     isExamplePage ? 'Intangible assets' : t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.intangibleAssets'),
     isExamplePage ? 'Property, plant and equipment' : t(
        'financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.propertyPlantAndEquipment',
      ),
     isExamplePage ? 'Right-of-use assets' : t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.rightOfUseAssets'),
     isExamplePage ? 'Goodwill' : t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.goodwill'),
     isExamplePage ? 'Long-term Investments' : t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.longTermInvestments'),
     isExamplePage ? 'Deferred Tax Assets' : t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.deferredTaxAssets'),
     isExamplePage ? 'Other non-current assets' : t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.otherNonCurrentAssets'),
    ];
  }, [t, i18n, isExamplePage]);
};

export const useGetAssetsTotalRows = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const currentAssetsTotalRowTitle = t('financialPlan.projectedBalanceSheets.assets.currentAssets.totalRowTitle');
    const nonCurrentAssetsTotalRowTitle = t(
      'financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.totalRowTitle',
    );
    const totalAssets = t('financialPlan.projectedBalanceSheets.assets.totalAssets');
    return { currentAssetsTotalRowTitle, nonCurrentAssetsTotalRowTitle, totalAssets };
  }, [t, i18n]);
};

export const currentAssetsVariants = [
  t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.cashEquivalents'),
  t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.shortTermInvestments'),
  t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.accountsReceivable'),
  t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.inventory'),
  t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.prepaidExpenses'),
  t('financialPlan.projectedBalanceSheets.assets.currentAssets.currentAssetsRowTitles.otherCurrentAssets'),
];

export const nonCurrentAssetsVariants = [
  t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.intangibleAssets'),
  t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.propertyPlantAndEquipment'),
  t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.rightOfUseAssets'),
  t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.goodwill'),
  t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.longTermInvestments'),
  t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.deferredTaxAssets'),
  t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.nonCurrentAssetsRowTitles.otherNonCurrentAssets'),
];

export const ASSETS_FORM_NAMES = {
  CURRENT_ASSETS_TABLE: 'currentAssetsTable',
  CURRENT_ASSETS_TABLE_TOTAL: 'currentAssetsTableTotal',
  NON_CURRENT_ASSETS_TABLE: 'nonCurrentAssetsTable',
  NON_CURRENT_ASSETS_TABLE_TOTAL: 'nonCurrentAssetsTableTotal',
  TOTAL_ASSETS: 'totalAssets',
} as const;

export const CURRENT_ASSETS_TABLE_TOTAL_NAMES = {
  CASH_EQUIVALENTS: 'cashEquivalents',
  SHORT_TERM_INVESTMENTS: 'shortTermInvestments',
  ACCOUNTS_RECEIVABLE: 'accountsReceivable',
  INVENTORY: 'inventory',
  PREPAID_EXPENSES: 'prepaidExpenses',
  OTHER_CURRENT_ASSETS: 'otherCurrentAssets',
} as const;

export const NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES = {
  INTANGIBLE_ASSETS: 'intangibleAssets',
  PROPERTY_PLANT_AND_EQUIPMENT: 'initialCostPPE',
  RIGHT_OF_USE_ASSETS: 'rightOfUseAssets',
  GOODWILL: 'goodwill',
  LONG_TERM_INVESTMENTS: 'longTermInvestments',
  DEFERRED_TAX_ASSETS: 'deferredTaxAssets',
  OTHER_NON_CURRENT_ASSETS: 'otherNonCurrentAssets',
} as const;


export interface IAssetsFormValuesType {
  [ASSETS_FORM_NAMES.CURRENT_ASSETS_TABLE]: {
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.CASH_EQUIVALENTS]: IPeriodsTableRow;
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.SHORT_TERM_INVESTMENTS]: IPeriodsTableRow;
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.ACCOUNTS_RECEIVABLE]: IPeriodsTableRow;
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.INVENTORY]: IPeriodsTableRow;
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.PREPAID_EXPENSES]: IPeriodsTableRow;
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.OTHER_CURRENT_ASSETS]: IPeriodsTableRow;
  };
  [ASSETS_FORM_NAMES.NON_CURRENT_ASSETS_TABLE]: {
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.INTANGIBLE_ASSETS]: IPeriodsTableRow;
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.PROPERTY_PLANT_AND_EQUIPMENT]: IPeriodsTableRow;
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.RIGHT_OF_USE_ASSETS]: IPeriodsTableRow;
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.GOODWILL]: IPeriodsTableRow;
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.LONG_TERM_INVESTMENTS]: IPeriodsTableRow;
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.DEFERRED_TAX_ASSETS]: IPeriodsTableRow;
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.OTHER_NON_CURRENT_ASSETS]: IPeriodsTableRow;
  };
  [ASSETS_FORM_NAMES.CURRENT_ASSETS_TABLE_TOTAL]: IPeriodsTableRow;
  [ASSETS_FORM_NAMES.NON_CURRENT_ASSETS_TABLE_TOTAL]: IPeriodsTableRow;
  [ASSETS_FORM_NAMES.TOTAL_ASSETS]: IPeriodsTableRow;
}

export const assetsInitialValues: IAssetsFormValuesType = {
  [ASSETS_FORM_NAMES.CURRENT_ASSETS_TABLE]: {
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.CASH_EQUIVALENTS]: initialPeriodsTableRow,
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.SHORT_TERM_INVESTMENTS]: initialPeriodsTableRow,
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.ACCOUNTS_RECEIVABLE]: initialPeriodsTableRow,
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.INVENTORY]: initialPeriodsTableRow,
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.PREPAID_EXPENSES]: initialPeriodsTableRow,
    [CURRENT_ASSETS_TABLE_TOTAL_NAMES.OTHER_CURRENT_ASSETS]: initialPeriodsTableRow,
  },
  [ASSETS_FORM_NAMES.NON_CURRENT_ASSETS_TABLE]: {
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.INTANGIBLE_ASSETS]: initialPeriodsTableRow,
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.PROPERTY_PLANT_AND_EQUIPMENT]: initialPeriodsTableRow,
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.RIGHT_OF_USE_ASSETS]: initialPeriodsTableRow,
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.GOODWILL]: initialPeriodsTableRow,
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.LONG_TERM_INVESTMENTS]: initialPeriodsTableRow,
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.DEFERRED_TAX_ASSETS]: initialPeriodsTableRow,
    [NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.OTHER_NON_CURRENT_ASSETS]: initialPeriodsTableRow,
  },
  [ASSETS_FORM_NAMES.CURRENT_ASSETS_TABLE_TOTAL]: initialPeriodsTableRow,
  [ASSETS_FORM_NAMES.NON_CURRENT_ASSETS_TABLE_TOTAL]: initialPeriodsTableRow,
  [ASSETS_FORM_NAMES.TOTAL_ASSETS]: initialPeriodsTableRow,
};
